/* eslint-disable no-restricted-syntax */
// Note: This file uses new Date() for UI state management (currentDate, calendarDate)
// and Date object manipulation for calendar calculations. These are necessary for
// React state and cannot be replaced with DateService methods.
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Add,
  ChevronLeft,
  ChevronRight,
  CalendarToday,
  Close,
  Delete,
  AccessTime,
  AutoFixHigh as GenerateIcon,
  Computer,
  PhotoCamera,
  Timeline,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { SmartScreenshotTimeEntry } from './SmartScreenshotTimeEntry';
import { PageHeader } from './PageHeader';
import { TASK_VALUES } from '../constants/tasks';
import timezoneService from '../services/timezoneService';
import { DateService } from '../utils/dateService';
import { format } from 'date-fns-tz';

interface TimeEntry {
  id: string;
  project: string;
  client: string;
  task: string;
  hours: number;
  date: string;
  isRunning?: boolean;
}

interface Project {
  id: string;
  name: string;
  description: string;
  categoryType: string;
  categories?: string[];
  clientId?: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

interface Client {
  id: string;
  name: string;
  email?: string;
  company?: string;
  phone?: string;
  address?: string;
}

interface ModernTimesheetProps {
  entries?: TimeEntry[];
}

export const ModernTimesheet: React.FC<ModernTimesheetProps> = ({ entries = [] }) => {
  const [currentDate, setCurrentDate] = useState(
    // eslint-disable-next-line no-restricted-syntax
    new Date()
  );
  const [viewMode, setViewMode] = useState('Day');
  const [showNewEntryDialog, setShowNewEntryDialog] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [manualHours, setManualHours] = useState<string>('');
  const [manualMinutes, setManualMinutes] = useState<string>('');
  const [entryDate, setEntryDate] = useState<string>(() => {
    // Fix: Use DateService for timezone-safe date formatting
    return DateService.getToday();
  });
  const [weekTimeValues, setWeekTimeValues] = useState<{[key: string]: string}>({});
  const [saving, setSaving] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarDate, setCalendarDate] = useState(
    // eslint-disable-next-line no-restricted-syntax
    new Date()
  );
  const [showAddProjectDialog, setShowAddProjectDialog] = useState(false);
  const [selectedProjectToAdd, setSelectedProjectToAdd] = useState('');
  const [selectedTaskToAdd, setSelectedTaskToAdd] = useState('');
  const [customTaskToAdd, setCustomTaskToAdd] = useState('');
  const [projectTaskCombinations, setProjectTaskCombinations] = useState<{[key: string]: string[]}>({});
  const [showWeekSummary, setShowWeekSummary] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Generate Time Dialog State
  const [projectSelectionOpen, setProjectSelectionOpen] = useState(false);
  const [suggestedTimeEntries, setSuggestedTimeEntries] = useState<any[]>([]);
  const [existingProjectsForDialog, setExistingProjectsForDialog] = useState<any[]>([]);
  const [existingTasksForDialog, setExistingTasksForDialog] = useState<string[]>([]);
  
  // Planning assignments state - for showing phase progress in time entries
  const [phaseAssignments, setPhaseAssignments] = useState<any[]>([]);

  // Handle Generate Time from Screenshots - opens dialog with AI suggestions
  const handleGenerateTimeEntries = async () => {
    const dateStr = DateService.dateToUserDateString(currentDate);
    
    console.log('🔥 Generate Time clicked for date:', dateStr);
    
    try {
      setGenerating(true);
      
      // Get AI-suggested time entries (preview mode)
      console.log('📡 Calling getSuggestedTimeEntries for date:', dateStr);
      const suggestionsRaw = await apiService.getSuggestedTimeEntries(dateStr);
      console.log('📡 API Response (raw):', suggestionsRaw);
      
      // FIXED: Use same filtering logic as ScreenshotHistory
      // Must filter by individual screenshot timestamps, not entry startTime
      let suggestions = suggestionsRaw;

      if (suggestionsRaw.suggestedEntries && suggestionsRaw.suggestedEntries.length > 0) {
        // Get all screenshot IDs from suggested entries
        const allScreenshotIds = new Set(
          suggestionsRaw.suggestedEntries.flatMap((e: any) => e.screenshotIds || [])
        );
        
        // Fetch screenshot data to check timestamps (same as ScreenshotHistory)
        const allScreenshotsResponse = await apiService.getScreenshots({ limit: 999 } as any);
        const allScreenshotsData = allScreenshotsResponse.data;
        
        // Filter screenshots by target date in user's timezone
        const validScreenshotIds = new Set(
          allScreenshotsData
            .filter((ss: any) => {
              const zonedDate = timezoneService.convertUTCToUserTime(ss.timestamp);
              const screenshotDateKey = format(zonedDate, 'yyyy-MM-dd', {
                timeZone: timezoneService.getUserTimezone()
              });
              return screenshotDateKey === dateStr;
            })
            .map((ss: any) => ss.id)
        );
        
        console.log(`📸 Frontend filtered: ${validScreenshotIds.size}/${allScreenshotIds.size} screenshots match ${dateStr} in user timezone`);
        
        // Filter and rebuild suggested entries to only include valid screenshots
        const filteredEntries = suggestionsRaw.suggestedEntries
          .map((entry: any) => {
            const validIds = (entry.screenshotIds || []).filter((id: string) => 
              validScreenshotIds.has(id)
            );
            
            if (validIds.length === 0) return null; // Skip entries with no valid screenshots
            
            // Recalculate duration based on valid screenshots only
            const newDuration = Math.max(validIds.length * 5, 15);
            
            // Get the actual screenshot objects for the valid IDs to calculate correct times
            const validScreenshots = allScreenshotsData.filter((ss: any) => 
              validIds.includes(ss.id)
            );
            
            // Sort by timestamp to get first and last
            validScreenshots.sort((a: any, b: any) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
            
            const firstScreenshot = validScreenshots[0];
            const lastScreenshot = validScreenshots[validScreenshots.length - 1];
            
            // Calculate new start and end times from filtered screenshots
            const newStartTime = firstScreenshot.timestamp;
            const newEndTime = new Date(new Date(firstScreenshot.timestamp).getTime() + newDuration * 60000).toISOString();
            
            // Extract activities from filtered screenshots
            const screenshotActivities: string[] = [];
            const allDetectedApps = new Set<string>();
            
            validScreenshots.forEach((ss: any) => {
              try {
                const analysis = ss.aiAnalysis || (ss.ai_analysis ? JSON.parse(ss.ai_analysis) : {});
                if (ss.activity) {
                  screenshotActivities.push(ss.activity);
                }
                if (analysis.detectedApps && Array.isArray(analysis.detectedApps)) {
                  analysis.detectedApps.forEach((app: string) => allDetectedApps.add(app));
                }
              } catch (e) {
                // Skip invalid data
              }
            });
            
            // Regenerate notes with correct numbers and times
            const startTimeFormatted = timezoneService.formatUserTime(
              timezoneService.convertUTCToUserTime(newStartTime), 
              { hour: '2-digit', minute: '2-digit' }
            );
            const lastTimeFormatted = timezoneService.formatUserTime(
              timezoneService.convertUTCToUserTime(lastScreenshot.timestamp), 
              { hour: '2-digit', minute: '2-digit' }
            );
            
            const newNotes = `Generated from ${validIds.length} screenshots between ${startTimeFormatted} and ${lastTimeFormatted}.\n\nScreenshot activities:\n${screenshotActivities.slice(0, 3).join('\n')}${screenshotActivities.length > 3 ? `\n... and ${screenshotActivities.length - 3} more` : ''}\n\nApps used: ${Array.from(allDetectedApps).join(', ')}`;
            
            return {
              ...entry,
              screenshotIds: validIds,
              screenshotCount: validIds.length,
              duration: newDuration,
              startTime: newStartTime,
              endTime: newEndTime,
              detectedApps: Array.from(allDetectedApps),
              notes: newNotes
            };
          })
          .filter((e: any) => e !== null);
        
        suggestions = {
          ...suggestionsRaw,
          suggestedEntries: filteredEntries,
          screenshotsAnalyzed: validScreenshotIds.size
        };
        
        console.log(`✅ Frontend filtered suggestions: ${suggestions.suggestedEntries.length} entries, ${suggestions.screenshotsAnalyzed} screenshots for ${dateStr}`);
      }
      
      if (suggestions.suggestedEntries && suggestions.suggestedEntries.length > 0) {
        // Load existing projects for selection
        const projectsList = await apiService.getProjects();
        setExistingProjectsForDialog(projectsList);
        
        // Process suggestions and auto-assign matching projects
        const processedEntries = suggestions.suggestedEntries.map((entry: any) => {
          if (entry.suggestedProjectName) {
            const sortedProjects = [...projectsList].sort((a: any, b: any) => {
              const dateA = new Date(a.createdAt || a.created_at || 0);
              const dateB = new Date(b.createdAt || b.created_at || 0);
              return dateB.getTime() - dateA.getTime();
            });
            
            const matchingProject = sortedProjects.find((p: any) => {
              const projectName = p.name.toLowerCase();
              const suggestedName = entry.suggestedProjectName.toLowerCase();
              return projectName.includes(suggestedName) || suggestedName.includes(projectName);
            });
            
            if (matchingProject) {
              return { ...entry, selectedProjectId: matchingProject.id };
            } else {
              return { ...entry, selectedProjectId: `new_${entry.suggestedProjectName.toLowerCase().replace(/\s+/g, '_')}` };
            }
          }
          return entry;
        });
        
        setSuggestedTimeEntries(processedEntries);
        
        // Load existing tasks
        const allTimeEntries = await apiService.getTimeEntries();
        const uniqueTasks = Array.from(new Set(
          allTimeEntries.map((entry: any) => entry.task || entry.description).filter((task: string) => task && task.trim() !== '')
        )) as string[];
        setExistingTasksForDialog(uniqueTasks);
        
        // Open dialog
        setProjectSelectionOpen(true);
      } else {
        alert('No screenshots found for this date. Make sure you have screenshots captured before trying to generate time entries.');
      }
    } catch (error: any) {
      console.error('❌ Error generating time entries:', error);
      alert(`Failed to get suggested time entries: ${error.message || 'Unknown error'}`);
    } finally {
      setGenerating(false);
    }
  };

  // Handle creating time entries from dialog
  const handleCreateSuggestedTimeEntries = async () => {
    try {
      setGenerating(true);

      let createdCount = 0;
      const targetDate = DateService.dateToUserDateString(currentDate);
      const allScreenshotIds: string[] = []; // Collect all screenshot IDs for marking

      for (const entry of suggestedTimeEntries) {
        if (!entry.selectedProjectId) continue;

        let projectId = entry.selectedProjectId;

        // If it's a new project, create it first
        if (entry.selectedProjectId.startsWith('new_')) {
          const newProject = await apiService.createProject({
            name: entry.suggestedProjectName,
            description: `Auto-generated project for ${entry.workType} work`,
            categoryType: entry.workType.toLowerCase()
          });
          projectId = newProject.id;
        }

        // Calculate timestamps
        const userStartTime = timezoneService.convertUTCToUserTime(entry.startTime);
        const startHours = userStartTime.getHours();
        const startMinutes = userStartTime.getMinutes();

        const localStartTimestamp = `${targetDate}T${String(startHours).padStart(2, '0')}:${String(startMinutes).padStart(2, '0')}:00`;
        const localStartDate = new Date(localStartTimestamp);
        const correctedStartTime = timezoneService.convertUserTimeToUTC(localStartDate);

        const localEndDate = new Date(localStartDate.getTime() + entry.duration * 60000);
        const correctedEndTime = timezoneService.convertUserTimeToUTC(localEndDate);

        // Create the time entry
        await apiService.createTimeEntry({
          projectId: projectId,
          task: entry.selectedTask || entry.workType,
          description: entry.description,
          startTime: correctedStartTime,
          endTime: correctedEndTime,
          duration: entry.duration,
          notes: entry.notes
        });

        // Collect screenshot IDs from this entry
        if (entry.screenshotIds && Array.isArray(entry.screenshotIds)) {
          allScreenshotIds.push(...entry.screenshotIds);
        }

        createdCount++;
      }

      // Mark all used screenshots as processed (so they won't appear in next "Generate Time")
      if (allScreenshotIds.length > 0) {
        try {
          console.log(`🏷️ Marking ${allScreenshotIds.length} screenshots as processed...`);
          await apiService.markScreenshotsAsProcessed(allScreenshotIds);
          console.log(`✅ Screenshots marked as processed`);
        } catch (markError) {
          console.error('⚠️ Failed to mark screenshots as processed:', markError);
          // Don't fail the whole operation if marking fails
        }
      }

      // Close dialog and refresh
      setProjectSelectionOpen(false);
      setSuggestedTimeEntries([]);

      // Refresh time entries
      const updatedTimeEntries = await apiService.getTimeEntries();
      setTimeEntries(updatedTimeEntries);

      alert(`✅ Successfully created ${createdCount} time entries!`);
      
    } catch (error: any) {
      console.error('Failed to create time entries:', error);
      alert(`Failed to create time entries: ${error.message || 'Unknown error'}`);
    } finally {
      setGenerating(false);
    }
  };

  // Fetch projects, clients, time entries and phase assignments on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectsData, clientsData, timeEntriesData] = await Promise.all([
          apiService.getProjects(),
          apiService.getClients(),
          apiService.getTimeEntries()
        ]);
        setProjects(projectsData);
        setClients(clientsData);
        setTimeEntries(timeEntriesData);
        
        // Fetch planning assignments (silently fail if not available)
        try {
          const assignmentsData = await apiService.getMyAssignments();
          setPhaseAssignments(assignmentsData || []);
        } catch (assignmentError) {
          console.log('No active planning assignments');
          setPhaseAssignments([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  
  // Helper: Get phase assignment for a project
  const getPhaseForProject = (projectId: string) => {
    return phaseAssignments.find((a: any) => a.projectId === projectId);
  };

  // Helper function to format minutes to hours:minutes
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  // Calculate time for a specific date
  // FIX: Convert UTC start_time to user timezone before comparing dates
  const getTimeForDate = (date: Date): number => {
    const dateStr = DateService.dateToUserDateString(date);
    return timeEntries
      .filter(entry => {
        if (!entry.start_time) return false;
        // Convert UTC timestamp to user's timezone for correct date grouping
        const userDate = timezoneService.convertUTCToUserTime(entry.start_time);
        const entryDateStr = DateService.dateToUserDateString(userDate);
        return entryDateStr === dateStr;
      })
      .reduce((total, entry) => total + (entry.duration || 0), 0);
  };

  // Get time entries for a specific date (using user's timezone for grouping)
  // FIX: Use DateService.dateToUserDateString for consistent timezone-aware date comparison
  const getTimeEntriesForDate = (date: Date) => {
    const dateStr = DateService.dateToUserDateString(date);
    return timeEntries
      .filter(entry => {
        if (!entry.start_time) return false;
        // Convert UTC timestamp to user's timezone for correct date grouping
        const userDate = timezoneService.convertUTCToUserTime(entry.start_time);
        const entryDateStr = DateService.dateToUserDateString(userDate);
        return entryDateStr === dateStr;
      })
          .map(entry => {
            const project = projects.find(p => p.id === entry.project_id);
            const client = project?.clientId ? clients.find(c => c.id === project.clientId) : null;
            // Use the task as the category label if available, else fallback to project categoryType
            // Also, if task is 'General' or empty, show project categoryType
            let categoryLabel = project?.categoryType || 'General';
            if (entry.task && entry.task.toLowerCase() !== 'general' && entry.task.trim() !== '') {
              categoryLabel = entry.task;
            }
            return {
              ...entry,
              project_name: project?.name || 'Unknown Project',
              project_category: categoryLabel,
              client_name: client?.name || null,
              client_company: client?.company || null
            };
          })
          .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
      };


  // Calculate total time for the week
  const getWeekTotal = (): number => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1);
    
    let total = 0;
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      total += getTimeForDate(day);
    }
    return total;
  };

  // Calculate total time for today
  const getTodayTotal = (): number => {
    return getTimeForDate(currentDate);
  };

  const formatDate = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    };
    return timezoneService.formatUserDate(date, options);
  };

  const formatWeekRange = (date: Date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay() + 1); // Monday
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
    
    return `${startOfWeek.getDate()} – ${endOfWeek.getDate()} ${timezoneService.formatUserDate(endOfWeek, { month: 'short' })} ${endOfWeek.getFullYear()}`;
  };

  const getDaysOfWeek = () => {
    const days = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1);
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const handleStartTimer = () => {
    setShowNewEntryDialog(true);
    // Reset form state
    setSelectedClient('');
    setSelectedProject('');
    setSelectedTask('');
    setNotes('');
    setManualHours('');
    setManualMinutes('');
    // Fix: Use dateToUserDateString for timezone-safe date formatting
    setEntryDate(DateService.dateToUserDateString(currentDate));
    setEditingEntryId(null);
  };


  // Handle calendar button click
  const handleCalendarClick = () => {
    setCalendarDate(new Date(currentDate));
    setShowDatePicker(true);
  };

  // Get calendar days for the month
  const getCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    
    // Start from Monday of the week containing the first day
    const startDate = new Date(firstDay);
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0
    startDate.setDate(firstDay.getDate() - daysToSubtract);
    
    // Generate 42 days (6 weeks)
    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    
    return days;
  };

  // Handle date selection from calendar
  const handleDateSelect = (selectedDate: Date) => {
    setCurrentDate(new Date(selectedDate));
    setShowDatePicker(false);
  };

  // Handle week selection from calendar
  const handleWeekSelect = (selectedDate: Date) => {
    setCurrentDate(new Date(selectedDate));
    setViewMode('Week');
    setShowDatePicker(false);
  };

  // Navigate calendar month
  const navigateCalendarMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(calendarDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCalendarDate(newDate);
  };

  // Handle client selection change (cascade to reset project and task)
  const handleClientChange = (clientId: string) => {
    setSelectedClient(clientId);
    setSelectedProject(''); // Reset project when client changes
    setSelectedTask(''); // Reset task when client changes
  };

  // Handle project selection change (cascade to reset task)
  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
    setSelectedTask(''); // Reset task when project changes
  };

  // Get projects filtered by selected client
  const getFilteredProjects = (): Project[] => {
    if (!selectedClient) {
      return projects; // Show all projects if no client selected
    }
    return projects.filter(project => project.clientId === selectedClient);
  };

  // Get available tasks for selected project
  const getAvailableTasks = (): string[] => {
    if (!selectedProject) {
      return TASK_VALUES; // Use centralized default tasks when no project selected
    }
    
    const project = projects.find(p => p.id === selectedProject);
    if (!project) {
      return []; // Return empty array if project not found
    }
    
    // Start with the project's primary categoryType
    const tasks = [project.categoryType];
    
    // Add additional categories if they exist
    if (project.categories && project.categories.length > 0) {
      project.categories.forEach(category => {
        if (category && category.trim() !== '' && !tasks.includes(category)) {
          tasks.push(category);
        }
      });
    }
    
    // Return ONLY the tasks specifically configured for this project
    return tasks.filter(task => task && task.trim() !== '');
  };

  // Handle add project row
  const handleAddProjectRow = () => {
    setShowAddProjectDialog(true);
  };

  // Get existing tasks for a project in current week
  const getExistingTasksForProject = (projectId: string): string[] => {
    const weekDays = getDaysOfWeek();
    const existingTasks = new Set<string>();
    
    weekDays.forEach(day => {
      const dayStr = DateService.dateToUserDateString(day);
      const dayEntries = timeEntries.filter(entry => 
        entry.project_id === projectId && 
        entry.start_time && 
        entry.start_time.startsWith(dayStr)
      );
      dayEntries.forEach(entry => {
        if (entry.task && entry.task.trim() !== '') {
          existingTasks.add(entry.task);
        }
      });
    });
    
    return Array.from(existingTasks);
  };

  // Handle add project/task row to week view
  const handleAddProjectTaskRow = () => {
    if (!selectedProjectToAdd) {
      alert('Please select a project');
      return;
    }

    const finalTask = customTaskToAdd.trim() || selectedTaskToAdd || 'General';
    
    // Create a unique key for this project-task combination
    const projectTaskKey = `${selectedProjectToAdd}-${finalTask}`;
    
    // Add to project-task combinations for week view
    setProjectTaskCombinations(prev => ({
      ...prev,
      [projectTaskKey]: [selectedProjectToAdd, finalTask]
    }));
    
    // Close dialog and reset form
    setShowAddProjectDialog(false);
    setSelectedProjectToAdd('');
    setSelectedTaskToAdd('');
    setCustomTaskToAdd('');
  };


  // Get time value for a specific project-task combination and day
  const getWeekTimeValue = (projectId: string, taskName: string, dayIndex: number, weekDays: Date[]): string => {
    const key = `${projectId}-${taskName}-${dayIndex}`;
    
    // Check if there's a pending edit value
    if (weekTimeValues[key] !== undefined) {
      return weekTimeValues[key];
    }
    
    // Otherwise get the actual value from database
    const day = weekDays[dayIndex];
    const dayTimeEntries = timeEntries.filter(entry => 
      entry.project_id === projectId && 
      entry.start_time && 
      entry.start_time.startsWith(DateService.dateToUserDateString(day)) &&
      (entry.task || 'General') === taskName
    );
    const dayTotal = dayTimeEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
    return dayTotal > 0 ? (dayTotal / 60).toFixed(1) : '';
  };

  // Save week time entries
  const handleSaveWeekTime = async () => {
    setSaving(true);
    try {
      const weekDays = getDaysOfWeek();
      
      // Process each changed time value
      for (const [key, value] of Object.entries(weekTimeValues)) {
        if (value && parseFloat(value) > 0) {
          console.log('Processing key:', key, 'value:', value);
          // Key format is: projectId-taskName-dayIndex
          const parts = key.split('-');
          const dayIndex = parseInt(parts[parts.length - 1]);
          const taskName = parts[parts.length - 2];
          const projectId = parts.slice(0, -2).join('-'); // Rejoin in case projectId has hyphens
          
          console.log('Split result - projectId:', projectId, 'taskName:', taskName, 'dayIndex:', dayIndex);
          
          // Validate that dayIndex is a valid number
          if (isNaN(dayIndex) || dayIndex < 0 || dayIndex >= weekDays.length) {
            console.error(`Invalid day index: ${dayIndex}, weekDays length:`, weekDays.length, 'key:', key);
            continue;
          }
          
          const day = weekDays[dayIndex];
          
          // Validate that day exists
          if (!day) {
            console.error(`Day is undefined for index: ${dayIndex}, weekDays:`, weekDays);
            continue;
          }
          
          const hours = parseFloat(value);
          const totalMinutes = Math.round(hours * 60);
          const dayString = DateService.dateToUserDateString(day);
          
          // Check if entry already exists for this project, task and day
          const existingEntry = timeEntries.find(entry => 
            entry.project_id === projectId && 
            entry.start_time && 
            entry.start_time.startsWith(dayString) &&
            (entry.task || 'General') === taskName
          );
          
          if (existingEntry) {
            // Update existing entry
            const timeEntryData = {
              projectId: projectId,
              task: taskName,
              description: 'Week view entry',
              startTime: existingEntry.start_time,
              duration: totalMinutes
            };
            await apiService.updateTimeEntry(existingEntry.id, timeEntryData);
          } else {
            // Create new entry
            // FIX: Create timestamps correctly using user timezone
            const userDateTime = new Date(`${DateService.dateToUserDateString(day)}T09:00:00`);
            const startTimeUTC = timezoneService.convertUserTimeToUTC(userDateTime);
            const endDateTime = new Date(userDateTime.getTime() + (totalMinutes * 60 * 1000));
            const endTimeUTC = timezoneService.convertUserTimeToUTC(endDateTime);
            
            const timeEntryData = {
              projectId: projectId,
              task: taskName,
              description: 'Week view entry',
              aiDescription: 'Week view entry',
              startTime: startTimeUTC,
              endTime: endTimeUTC,
              duration: totalMinutes
            };
            await apiService.createTimeEntry(timeEntryData);
          }
        }
      }
      
      // Refresh time entries after all operations
      const updatedTimeEntries = await apiService.getTimeEntries();
      setTimeEntries(updatedTimeEntries);
      
      // Clear pending changes
      setWeekTimeValues({});
      
      // Removed popup alert - silent save
    } catch (error) {
      console.error('Error saving week time entries:', error);
      // Log more detailed error information
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      }
      alert(`Failed to save week time entries: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };


      const handleCreateTimeEntry = async () => {
        if (!selectedProject) {
          alert('Please select a project');
          return;
        }

        // Calculate duration in minutes
        const hours = parseInt(manualHours) || 0;
        const minutes = parseInt(manualMinutes) || 0;
        const totalMinutes = (hours * 60) + minutes;

        if (totalMinutes <= 0) {
          alert('Please enter a valid duration (hours and/or minutes)');
          return;
        }

        try {
          setCreating(true);
          
          // FIX: Create timestamps correctly using user timezone
          // Get current time in user timezone
          const now = new Date();
          const currentTime = now.toTimeString().split(' ')[0]; // HH:MM:SS
          
          // Create date-time string in user's local timezone
          const userDateTime = new Date(`${entryDate}T${currentTime}`);
          
          // Convert to UTC for backend storage
          const startTimeUTC = timezoneService.convertUserTimeToUTC(userDateTime);
          const endDateTime = new Date(userDateTime.getTime() + (totalMinutes * 60 * 1000));
          const endTimeUTC = timezoneService.convertUserTimeToUTC(endDateTime);
          
          const timeEntryData = {
            projectId: selectedProject,
            task: selectedTask || 'General',
            description: notes,
            notes: notes,
            startTime: startTimeUTC,
            endTime: endTimeUTC,
            duration: totalMinutes
          };

          if (editingEntryId) {
            await apiService.updateTimeEntry(editingEntryId, timeEntryData);
          } else {
            await apiService.createTimeEntry(timeEntryData);
          }
          
          // Refresh time entries
          const updatedTimeEntries = await apiService.getTimeEntries();
          setTimeEntries(updatedTimeEntries);
          
          // Close dialog and reset form
          setShowNewEntryDialog(false);
          setSelectedClient('');
          setSelectedProject('');
          setSelectedTask('');
          setNotes('');
          setManualHours('');
          setManualMinutes('');
          // Fix: Use DateService for timezone-safe date formatting
          setEntryDate(DateService.getToday());
          setEditingEntryId(null);
          
          // Removed success alert for better UX
        } catch (error) {
          console.error('Error saving time entry:', error);
          alert('Failed to save time entry. Please try again.');
        } finally {
          setCreating(false);
        }
      };
       
      const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
       
      const handleEditEntry = (entry: any) => {
        // Find the project to get its clientId
        const project = projects.find(p => p.id === entry.project_id);
        if (project && project.clientId) {
          setSelectedClient(project.clientId);
        } else {
          setSelectedClient('');
        }
        
        setSelectedProject(entry.project_id);
        
        // Set task from entry.task field
        setSelectedTask(entry.task || 'General');
        
        // Set notes from notes field, fallback to ai_description
        setNotes(entry.notes || entry.ai_description || '');
        
        setManualHours(Math.floor((entry.duration || 0) / 60).toString());
        setManualMinutes(((entry.duration || 0) % 60).toString());
        setEntryDate(entry.start_time ? entry.start_time.split('T')[0] : DateService.getToday());
        setEditingEntryId(entry.id);
        setShowNewEntryDialog(true);
      };

      const handleDeleteEntry = async (entryId: string) => {
        if (window.confirm('Are you sure you want to delete this time entry?')) {
          try {
            await apiService.deleteTimeEntry(entryId);
            // Refresh time entries
            const updatedTimeEntries = await apiService.getTimeEntries();
            setTimeEntries(updatedTimeEntries);
          } catch (error) {
            console.error('Error deleting time entry:', error);
            alert('Failed to delete time entry. Please try again.');
          }
        }
      };

  // New Entry Dialog JSX
  const newEntryDialogJSX = (
    <Dialog 
      open={showNewEntryDialog} 
      onClose={() => setShowNewEntryDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '600px',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
        {editingEntryId ? 'Edit' : 'New'} time entry for {formatDate(currentDate)}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Active Phase Info - show when project is selected */}
          {selectedProject && (() => {
            const phase = getPhaseForProject(selectedProject);
            if (!phase) return null;
            
            const progressColor = phase.progress >= 100 ? '#e74c3c' : phase.progress >= 80 ? '#f39c12' : '#27ae60';
            
            return (
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f0f9ff', 
                borderRadius: 2, 
                border: `1px solid ${progressColor}`,
                borderLeft: `4px solid ${progressColor}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Timeline sx={{ fontSize: 18, color: progressColor }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                    Active Phase: {phase.phaseName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1, height: 8, backgroundColor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ 
                      width: `${Math.min(phase.progress, 100)}%`, 
                      height: '100%', 
                      backgroundColor: progressColor,
                      borderRadius: 1
                    }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: progressColor, minWidth: 80 }}>
                    {phase.actualHours}h / {phase.plannedHours}h
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {phase.remainingHours}h remaining • Status: {phase.phaseStatus}
                </Typography>
              </Box>
            );
          })()}
          
          {/* Date Selection */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              variant="outlined"
            />
          </Box>

          {/* Client Selection */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Client
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedClient}
                onChange={(e) => handleClientChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select a client (optional)</em>
                </MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}{client.company ? ` - ${client.company}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Project Selection */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Project
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedProject}
                onChange={(e) => handleProjectChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>Select a project</em>
                </MenuItem>
                {getFilteredProjects().map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                    {selectedClient && project.clientId !== selectedClient ? 
                      ` (${clients.find(c => c.id === project.clientId)?.name || 'No Client'})` : 
                      ''
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedClient && getFilteredProjects().length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
                No projects found for selected client. You can still select from all projects above.
              </Typography>
            )}
          </Box>

          {/* Task Selection */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Task
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.target.value)}
                displayEmpty
                disabled={!selectedProject}
              >
                <MenuItem value="">
                  <em>{selectedProject ? 'Select a task' : 'Select project first'}</em>
                </MenuItem>
                {getAvailableTasks().map((task) => (
                  <MenuItem key={task} value={task}>
                    {task}
                    {selectedProject && projects.find(p => p.id === selectedProject)?.categoryType === task ? 
                      ' (Primary)' : ''
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedProject && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, fontSize: '0.875rem' }}>
                Tasks are based on the selected project's configuration.
                Primary task: <strong>{projects.find(p => p.id === selectedProject)?.categoryType || 'General'}</strong>
              </Typography>
            )}
          </Box>

          {/* Time Duration */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Duration
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Hours"
                type="number"
                value={manualHours}
                onChange={(e) => setManualHours(e.target.value)}
                variant="outlined"
                size="small"
                inputProps={{ min: 0, max: 24 }}
                sx={{ width: 100 }}
              />
              <Typography variant="body2">h</Typography>
              <TextField
                label="Minutes"
                type="number"
                value={manualMinutes}
                onChange={(e) => setManualMinutes(e.target.value)}
                variant="outlined"
                size="small"
                inputProps={{ min: 0, max: 59 }}
                sx={{ width: 100 }}
              />
              <Typography variant="body2">m</Typography>
            </Box>
          </Box>

          {/* Notes */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Notes (optional)
            </Typography>
            <TextField
              fullWidth
              placeholder="Add notes about this time entry..."
              variant="outlined"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateTimeEntry}
          disabled={creating || !selectedProject || (parseInt(manualHours) || 0) + (parseInt(manualMinutes) || 0) <= 0}
          sx={{
            backgroundColor: '#27ae60',
            '&:hover': {
              backgroundColor: '#219a52',
            },
            textTransform: 'none',
            px: 3,
          }}
        >
          {creating 
            ? (editingEntryId ? 'Updating...' : 'Creating...') 
            : (editingEntryId ? 'Update Time Entry' : 'Add Time Entry')}
        </Button>
        <Button 
          onClick={() => setShowNewEntryDialog(false)}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (viewMode === 'Week') {
    const weekDays = getDaysOfWeek();
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get all unique project-task combinations from time entries for this week
    const getProjectTaskRows = () => {
      const projectTaskMap = new Map<string, { project: Project; task: string; hasData: boolean }>();
      
      // Add combinations from existing time entries
      weekDays.forEach(day => {
        const dayStr = DateService.dateToUserDateString(day);
        const dayEntries = timeEntries.filter(entry => 
          entry.start_time && 
          entry.start_time.startsWith(dayStr)
        );
        
        dayEntries.forEach(entry => {
          const project = projects.find(p => p.id === entry.project_id);
          if (project) {
            const task = entry.task || 'General';
            const key = `${project.id}-${task}`;
            projectTaskMap.set(key, {
              project,
              task,
              hasData: true
            });
          }
        });
      });
      
      // Add combinations from manually added project-task combinations
      Object.entries(projectTaskCombinations).forEach(([key, [projectId, taskName]]) => {
        const project = projects.find(p => p.id === projectId);
        if (project) {
          const mapKey = `${projectId}-${taskName}`;
          if (!projectTaskMap.has(mapKey)) {
            projectTaskMap.set(mapKey, {
              project,
              task: taskName,
              hasData: false
            });
          }
        }
      });
      
      return Array.from(projectTaskMap.values());
    };
    
    const projectTaskRows = getProjectTaskRows();
    
    return (
      <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <PageHeader 
          icon={AccessTime} 
          title="Time"
          subtitle={`${(() => {
            const today = new Date();
            const currentWeekStart = new Date(currentDate);
            currentWeekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1);
            const todayWeekStart = new Date(today);
            todayWeekStart.setDate(today.getDate() - today.getDay() + 1);
            
            const isSameWeek = currentWeekStart.toDateString() === todayWeekStart.toDateString();
            return isSameWeek ? 'This week' : 'Week';
          })()}: ${formatWeekRange(currentDate)}`}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() - 7)))}>
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={() => setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() + 7)))}>
              <ChevronRight />
            </IconButton>
            <IconButton onClick={handleCalendarClick}>
              <CalendarToday />
            </IconButton>
            <Button
              variant={(viewMode as string) === 'Day' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('Day')}
              sx={{ textTransform: 'none' }}
            >
              Day
            </Button>
            <Button
              variant={(viewMode as string) === 'Week' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('Week')}
              sx={{ 
                textTransform: 'none',
                ...(viewMode === 'Week' && {
                  backgroundColor: '#e67e22',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#d35400',
                  }
                })
              }}
            >
              Week
            </Button>
          </Box>
        </PageHeader>

        {/* Week View Table - Responsive Scroll Container */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            {/* Table Container with minimum width to ensure layout integrity */}
            <Box sx={{ minWidth: '1000px' }}>
              {/* Table Header */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '300px repeat(7, 1fr) 60px 40px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #e0e0e0'
              }}>
            <Box sx={{ p: 2 }}>
              {/* Empty header cell for project info */}
            </Box>
            {weekDays.map((day, index) => (
              <Box key={index} sx={{ p: 2, textAlign: 'center', borderLeft: '1px solid #e0e0e0' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {dayNames[index]}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {day.getDate().toString().padStart(2, '0')} {monthNames[day.getMonth()]}
                </Typography>
              </Box>
            ))}
            <Box sx={{ p: 2, textAlign: 'center', borderLeft: '1px solid #e0e0e0' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Total
              </Typography>
            </Box>
            <Box sx={{ p: 2, borderLeft: '1px solid #e0e0e0' }}>
              {/* Actions column */}
            </Box>
          </Box>

          {/* Project-Task Rows */}
          {projectTaskRows.length > 0 ? projectTaskRows.map((row, rowIndex) => {
            const borderColor = row.hasData ? '#27ae60' : '#e67e22';
            
            return (
              <Box key={`${row.project.id}-${row.task}`} sx={{ 
                display: 'grid', 
                gridTemplateColumns: '300px repeat(7, 1fr) 60px 40px',
                borderBottom: rowIndex < projectTaskRows.length - 1 ? '1px solid #e0e0e0' : 'none',
                '&:hover': {
                  backgroundColor: '#fafafa'
                }
              }}>
                {/* Project Info */}
                <Box sx={{ p: 2, borderLeft: `3px solid ${borderColor}` }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {row.project.name}
                    {row.task !== 'General' && ` - ${row.task}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {row.task}
                  </Typography>
                </Box>

                {/* Day Columns */}
                {weekDays.map((day, dayIndex) => {
                  const timeValue = getWeekTimeValue(row.project.id, row.task, dayIndex, weekDays);
                  
                  return (
                    <Box key={dayIndex} sx={{ 
                      p: 1, 
                      borderLeft: '1px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TextField
                        size="small"
                        value={timeValue}
                        placeholder="0"
                        variant="outlined"
                        type="number"
                        inputProps={{ 
                          min: 0, 
                          max: 24, 
                          step: 0.1 
                        }}
                        onChange={(e) => {
                          const key = `${row.project.id}-${row.task}-${dayIndex}`;
                          setWeekTimeValues(prev => ({
                            ...prev,
                            [key]: e.target.value
                          }));
                        }}
                        sx={{
                          width: '60px',
                          '& .MuiOutlinedInput-root': {
                            height: '32px',
                            '& input': {
                              textAlign: 'center',
                              padding: '6px 8px',
                              fontSize: '0.875rem'
                            }
                          }
                        }}
                      />
                    </Box>
                  );
                })}

                {/* Total Column */}
                <Box sx={{ 
                  p: 2, 
                  borderLeft: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(() => {
                      // Calculate total for this specific project-task combination
                      const total = weekDays.reduce((sum, day, dayIndex) => {
                        const dayStr = day.toISOString().split('T')[0];
                        const dayEntries = timeEntries.filter(entry => 
                          entry.project_id === row.project.id && 
                          entry.start_time && 
                          entry.start_time.startsWith(dayStr) &&
                          (entry.task || 'General') === row.task
                        );
                        return sum + dayEntries.reduce((daySum, entry) => daySum + (entry.duration || 0), 0);
                      }, 0);
                      return total > 0 ? (total / 60).toFixed(1) : '0';
                    })()}
                  </Typography>
                </Box>

                {/* Actions Column */}
                <Box sx={{ 
                  p: 1, 
                  borderLeft: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {!row.hasData && (
                    <IconButton 
                      size="small" 
                      sx={{ color: 'text.secondary' }}
                      onClick={() => {
                        const key = `${row.project.id}-${row.task}`;
                        setProjectTaskCombinations(prev => {
                          const newCombinations = { ...prev };
                          delete newCombinations[key];
                          return newCombinations;
                        });
                      }}
                    >
                      <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          }) : (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '300px repeat(7, 1fr) 60px 40px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <Box sx={{ p: 2, borderLeft: '3px solid #e67e22' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  No time entries for this week
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                  Add time entries or use "Add row" to get started
                </Typography>
              </Box>
              {weekDays.map((day, dayIndex) => (
                <Box key={dayIndex} sx={{ 
                  p: 1, 
                  borderLeft: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TextField
                    size="small"
                    placeholder="0"
                    variant="outlined"
                    disabled
                    sx={{
                      width: '60px',
                      '& .MuiOutlinedInput-root': {
                        height: '32px',
                        '& input': {
                          textAlign: 'center',
                          padding: '6px 8px',
                          fontSize: '0.875rem'
                        }
                      }
                    }}
                  />
                </Box>
              ))}
              <Box sx={{ 
                p: 2, 
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  0
                </Typography>
              </Box>
              <Box sx={{ 
                p: 1, 
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Empty actions cell */}
              </Box>
            </Box>
          )}

          {/* Totals Row */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '300px repeat(7, 1fr) 60px 40px',
            backgroundColor: '#f9f9f9',
            borderTop: '2px solid #e0e0e0'
          }}>
            <Box sx={{ p: 2 }}>
              {/* Empty cell */}
            </Box>
            {weekDays.map((day, dayIndex) => {
              const dayTotal = getTimeForDate(day);
              return (
                <Box key={dayIndex} sx={{ 
                  p: 2, 
                  borderLeft: '1px solid #e0e0e0',
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {dayTotal > 0 ? (dayTotal / 60).toFixed(1) : '0'}
                  </Typography>
                </Box>
              );
            })}
            <Box sx={{ 
              p: 2, 
              borderLeft: '1px solid #e0e0e0',
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {getWeekTotal() > 0 ? (getWeekTotal() / 60).toFixed(1) : '0'}
              </Typography>
            </Box>
            <Box sx={{ p: 2, borderLeft: '1px solid #e0e0e0' }}>
              {/* Empty cell */}
            </Box>
          </Box>
          </Box>
          </Box>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              startIcon={<Add />}
              variant="outlined"
              onClick={handleAddProjectRow}
              sx={{
                textTransform: 'none',
                color: '#27ae60',
                borderColor: '#27ae60',
                '&:hover': {
                  backgroundColor: '#27ae60',
                  color: 'white'
                }
              }}
            >
              Add row
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveWeekTime}
              disabled={saving}
              sx={{
                backgroundColor: '#27ae60',
                '&:hover': {
                  backgroundColor: '#219a52',
                },
                textTransform: 'none',
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <SmartScreenshotTimeEntry 
              onTimeEntryCreated={async () => {
                const updatedTimeEntries = await apiService.getTimeEntries();
                setTimeEntries(updatedTimeEntries);
              }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
              Last saved at 3:19pm
            </Typography>
          </Box>
        </Box>

        {/* Submit Button */}
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            sx={{ 
              textTransform: 'none',
              borderColor: '#e0e0e0',
              color: 'text.secondary'
            }}
          >
            Submit week for approval
          </Button>
        </Box>

        {newEntryDialogJSX}
        
        {/* Add Project Dialog */}
        <Dialog 
          open={showAddProjectDialog} 
          onClose={() => setShowAddProjectDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            }
          }}
        >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          Add Project Row to Week View
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Select Project *
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedProjectToAdd}
                  onChange={(e) => setSelectedProjectToAdd(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select a project</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name} - {project.categoryType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {selectedProjectToAdd && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Task/Category
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <Select
                    value={selectedTaskToAdd}
                    onChange={(e) => setSelectedTaskToAdd(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select existing task or create new</em>
                    </MenuItem>
                    {getExistingTasksForProject(selectedProjectToAdd).map((task) => (
                      <MenuItem key={task} value={task}>
                        {task} (existing)
                      </MenuItem>
                    ))}
                    <MenuItem value="Design">Design</MenuItem>
                    <MenuItem value="Development">Development</MenuItem>
                    <MenuItem value="Testing">Testing</MenuItem>
                    <MenuItem value="Meeting">Meeting</MenuItem>
                    <MenuItem value="Research">Research</MenuItem>
                    <MenuItem value="General">General</MenuItem>
                  </Select>
                </FormControl>
                
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Or create custom task:
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter custom task name"
                  value={customTaskToAdd}
                  onChange={(e) => setCustomTaskToAdd(e.target.value)}
                  variant="outlined"
                  size="small"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleAddProjectTaskRow}
            disabled={!selectedProjectToAdd}
            sx={{
              backgroundColor: '#27ae60',
              '&:hover': {
                backgroundColor: '#219a52',
              },
              textTransform: 'none',
              px: 3,
            }}
          >
            Add Row to Week View
          </Button>
          <Button 
            onClick={() => {
              setShowAddProjectDialog(false);
              setSelectedProjectToAdd('');
              setSelectedTaskToAdd('');
              setCustomTaskToAdd('');
            }}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
        </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Day View
  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <PageHeader 
        icon={AccessTime} 
        title="Time"
        subtitle={`${DateService.isSameDay(currentDate, new Date()) ? 'Today' : 'Day'}: ${formatDate(currentDate)}`}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() - 1)))}>
            <ChevronLeft />
          </IconButton>
          <IconButton onClick={() => setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() + 1)))}>
            <ChevronRight />
          </IconButton>
          <IconButton onClick={handleCalendarClick}>
            <CalendarToday />
          </IconButton>
          <Button
            variant={(viewMode as string) === 'Day' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('Day')}
            sx={{ textTransform: 'none' }}
          >
            Day
          </Button>
          <Button
            variant={(viewMode as string) === 'Week' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('Week')}
            sx={{ textTransform: 'none' }}
          >
            Week
          </Button>
        </Box>
      </PageHeader>

      {/* Improved Week Summary - Collapsible */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
            📊 Week Overview
          </Typography>
          <Button
            size="small"
            onClick={() => setShowWeekSummary(!showWeekSummary)}
            sx={{ 
              textTransform: 'none', 
              color: '#666',
              minWidth: 'auto',
              p: 0.5
            }}
          >
            {showWeekSummary ? '▼ Hide' : '▶ Show'}
          </Button>
        </Box>
        
        {showWeekSummary && (
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            {getDaysOfWeek().map((day, index) => {
              const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
              const timeForDay = getTimeForDate(day);
              const isToday = day.toDateString() === 
              // eslint-disable-next-line no-restricted-syntax
              new Date().toDateString();
              const isSelected = day.toDateString() === currentDate.toDateString();
              return (
                <Box 
                  key={index} 
                  onClick={() => setCurrentDate(new Date(day))}
                  sx={{ 
                    textAlign: 'center',
                    p: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: isSelected ? '#1976d2' : isToday ? '#e3f2fd' : 'transparent',
                    border: isSelected ? '2px solid #1976d2' : isToday ? '2px solid #90caf9' : '2px solid transparent',
                    '&:hover': {
                      backgroundColor: isSelected ? '#1565c0' : '#e3f2fd',
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ 
                    fontWeight: isSelected || isToday ? 600 : 500, 
                    mb: 0.5,
                    color: isSelected ? 'white' : isToday ? '#1976d2' : 'inherit'
                  }}>
                    {dayNames[index]}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: isSelected ? 'white' : isToday ? '#1976d2' : 'text.secondary',
                    fontWeight: isSelected || isToday ? 600 : 400
                  }}>
                    {formatDuration(timeForDay)}
                  </Typography>
                  {isToday && !isSelected && (
                    <Box sx={{ 
                      width: 6, 
                      height: 6, 
                      borderRadius: '50%', 
                      backgroundColor: '#1976d2',
                      mx: 'auto',
                      mt: 0.5
                    }} />
                  )}
                </Box>
              );
            })}
            <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 40 }} />
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#27ae60' }}>
                Week total
              </Typography>
              <Typography variant="body2" sx={{ color: '#27ae60', fontWeight: 600 }}>
                {formatDuration(getWeekTotal())}
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Time Entries for Selected Date */}
      {loading ? (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, textAlign: 'center' }}>
          <Typography>Loading time entries...</Typography>
        </Paper>
      ) : (() => {
        const todayEntries = getTimeEntriesForDate(currentDate);
        
        if (todayEntries.length === 0) {
          return (
            <Paper sx={{ 
              p: 4, 
              mb: 3, 
              borderRadius: 2, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: '2px dashed #dee2e6'
            }}>
              {/* Enhanced Empty State */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  color: '#495057', 
                  mb: 1, 
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  🚀 Ready to track your time?
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}>
                  No time entries for {formatDate(currentDate)}. Get started by choosing how you'd like to add your first entry:
                </Typography>
              </Box>

              {/* Primary Action Buttons with Better Hierarchy */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={handleStartTimer}
                  sx={{ 
                    textTransform: 'none',
                    backgroundColor: '#27ae60',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(39, 174, 96, 0.3)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#219a52',
                      boxShadow: '0 6px 16px rgba(39, 174, 96, 0.4)',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  ✏️ Manual Entry
                </Button>
                <SmartScreenshotTimeEntry 
                  selectedDate={currentDate.toISOString().split('T')[0]}
                  onTimeEntryCreated={async () => {
                    const updatedTimeEntries = await apiService.getTimeEntries();
                    setTimeEntries(updatedTimeEntries);
                  }}
                />
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<GenerateIcon />}
                  onClick={handleGenerateTimeEntries}
                  disabled={generating}
                  sx={{ 
                    textTransform: 'none',
                    borderColor: '#9b59b6',
                    color: '#9b59b6',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#9b59b6',
                      borderColor: '#9b59b6',
                      color: 'white',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  {generating ? 'Generating...' : '✨ Generate Time'}
                </Button>
              </Box>

              {/* Helpful Tips */}
              <Box sx={{ 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7',
                borderRadius: 2, 
                p: 2, 
                mt: 2,
                textAlign: 'left'
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#856404', 
                  fontWeight: 600, 
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  💡 Quick Tips:
                </Typography>
                <Box component="ul" sx={{ 
                  margin: 0, 
                  paddingLeft: 2, 
                  color: '#856404',
                  fontSize: '0.875rem',
                  lineHeight: 1.5
                }}>
                  <li><strong>Manual Entry:</strong> Perfect for planned work or when you know exactly what you did</li>
                  <li><strong>From Screenshots:</strong> Let AI analyze your screen activity and suggest time entries</li>
                  <li><strong>Pro tip:</strong> Screenshots work best when you have 5+ screenshots from your work session</li>
                </Box>
              </Box>
            </Paper>
          );
        }

        return (
          <>
            {todayEntries.map((entry) => {
              const phase = getPhaseForProject(entry.project_id);
              const progressColor = phase ? (phase.progress >= 100 ? '#e74c3c' : phase.progress >= 80 ? '#f39c12' : '#27ae60') : null;
              
              return (
              <Paper key={entry.id} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {entry.project_name}
                      </Typography>
                      {entry.client_name && (
                        <>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            •
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            {entry.client_name}{entry.client_company ? ` (${entry.client_company})` : ''}
                          </Typography>
                        </>
                      )}
                    </Box>
                    {(entry.notes || entry.ai_description) && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontStyle: 'italic' }}>
                        "{entry.notes || entry.ai_description}"
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip 
                        label={entry.task || 'General'} 
                        size="small" 
                        sx={{ 
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          textTransform: 'capitalize'
                        }} 
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {timezoneService.formatUserTime(new Date(entry.start_time), { 
                          hour: '2-digit', 
                          minute: '2-digit'
                        })}
                      </Typography>
                      {/* Inline Phase Progress */}
                      {phase && (
                        <Tooltip title={`${phase.phaseName}: ${phase.actualHours}h / ${phase.plannedHours}h (${phase.remainingHours}h left)`}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                            <Timeline sx={{ fontSize: 14, color: progressColor }} />
                            <Box sx={{ width: 60, height: 6, backgroundColor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                              <Box sx={{ 
                                width: `${Math.min(phase.progress, 100)}%`, 
                                height: '100%', 
                                backgroundColor: progressColor,
                                borderRadius: 1
                              }} />
                            </Box>
                            <Typography variant="caption" sx={{ color: progressColor, fontWeight: 600, fontSize: '0.7rem' }}>
                              {phase.actualHours}h/{phase.plannedHours}h
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatDuration(entry.duration || 0)}
                    </Typography>
                    <Button
                      variant="text"
                      sx={{ textTransform: 'none' }}
                      onClick={() => handleEditEntry(entry)}
                    >
                      Edit
                    </Button>
                    <IconButton
                      size="small"
                      sx={{ color: 'text.secondary' }}
                      onClick={() => handleDeleteEntry(entry.id)}
                    >
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            )})}
            
            {/* Add Time Entry Options - Always show */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                Add More Time
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleStartTimer}
                  sx={{ 
                    textTransform: 'none',
                    backgroundColor: '#27ae60',
                    '&:hover': {
                      backgroundColor: '#219a52',
                    },
                  }}
                >
                  Add Time Entry
                </Button>
                <SmartScreenshotTimeEntry 
                  selectedDate={currentDate.toISOString().split('T')[0]}
                  onTimeEntryCreated={async () => {
                    const updatedTimeEntries = await apiService.getTimeEntries();
                    setTimeEntries(updatedTimeEntries);
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<GenerateIcon />}
                  onClick={handleGenerateTimeEntries}
                  disabled={generating}
                  sx={{ 
                    textTransform: 'none',
                    borderColor: '#9b59b6',
                    color: '#9b59b6',
                    '&:hover': {
                      backgroundColor: '#9b59b6',
                      borderColor: '#9b59b6',
                      color: 'white',
                    }
                  }}
                >
                  {generating ? 'Generating...' : '✨ Generate Time'}
                </Button>
              </Box>
            </Paper>
          </>
        );
      })()}

      {/* Total */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Total:
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {formatDuration(getTodayTotal())}
        </Typography>
      </Box>

      {/* Submit Button */}
      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          Submit week for approval
        </Button>
      </Box>

      {newEntryDialogJSX}

      {/* Generate Time Suggestion Dialog */}
      <Dialog
        open={projectSelectionOpen}
        onClose={() => setProjectSelectionOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Review and Create Time Entries
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
            AI has analyzed your screenshots and suggests the following time entries. Review and select projects for each entry:
          </Typography>

          {suggestedTimeEntries.map((entry, index) => (
            <Card key={entry.id || index} sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
              <CardContent sx={{ pb: 2 }}>
                {/* Header with time and duration */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 2,
                  pb: 2,
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: '700', color: '#1a1a1a', mb: 0.5 }}>
                      {entry.description || entry.workType}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', fontWeight: '500' }}>
                      {entry.startTime ? timezoneService.formatUserTime(timezoneService.convertUTCToUserTime(entry.startTime), { 
                        hour: '2-digit', 
                        minute: '2-digit'
                      }) : 'N/A'} - {entry.endTime ? timezoneService.formatUserTime(timezoneService.convertUTCToUserTime(entry.endTime), { 
                        hour: '2-digit', 
                        minute: '2-digit'
                      }) : 'N/A'}
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${Math.round(entry.duration)} min`}
                    sx={{ 
                      backgroundColor: '#e3f2fd', 
                      color: '#1976d2',
                      fontWeight: '600'
                    }}
                  />
                </Box>

                {/* Apps and screenshots info */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Computer sx={{ fontSize: '16px', color: '#666' }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      <strong>Apps:</strong> {entry.detectedApps?.join(', ') || 'Unknown'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhotoCamera sx={{ fontSize: '16px', color: '#666' }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      <strong>From:</strong> {entry.screenshotCount || 0} screenshots
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Select Project:
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={entry.selectedProjectId || ''}
                      onChange={(e: any) => {
                        const updatedEntries = [...suggestedTimeEntries];
                        updatedEntries[index].selectedProjectId = e.target.value;
                        setSuggestedTimeEntries(updatedEntries);
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a project...</em>
                      </MenuItem>
                      {existingProjectsForDialog.map((project: any) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Task:
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={entry.selectedTask || entry.workType || ''}
                      onChange={(e: any) => {
                        const updatedEntries = [...suggestedTimeEntries];
                        updatedEntries[index].selectedTask = e.target.value;
                        setSuggestedTimeEntries(updatedEntries);
                      }}
                    >
                      <MenuItem value={entry.workType}>
                        {entry.workType} (AI Suggestion)
                      </MenuItem>
                      {existingTasksForDialog.map((task: string) => (
                        <MenuItem key={task} value={task}>
                          {task}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {entry.notes && (
                  <Box sx={{ backgroundColor: '#f9f9f9', p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                      Notes:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-line' }}>
                      {entry.notes}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setProjectSelectionOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateSuggestedTimeEntries}
              disabled={generating || suggestedTimeEntries.some((entry: any) => !entry.selectedProjectId)}
              sx={{
                backgroundColor: '#27ae60',
                '&:hover': {
                  backgroundColor: '#229954'
                }
              }}
            >
              {generating ? 'Creating...' : `Create ${suggestedTimeEntries.length} Time Entries`}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Calendar Dialog */}
      <Dialog 
        open={showDatePicker} 
        onClose={() => setShowDatePicker(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigateCalendarMonth('prev')} size="small">
              <ChevronLeft />
            </IconButton>
            <Typography variant="h6">
              {timezoneService.formatUserDate(calendarDate, { month: 'long', year: 'numeric' })}
            </Typography>
            <IconButton onClick={() => navigateCalendarMonth('next')} size="small">
              <ChevronRight />
            </IconButton>
          </Box>
          <IconButton onClick={() => setShowDatePicker(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2 }}>
          {/* Calendar Grid */}
          <Box sx={{ width: '100%' }}>
            {/* Day Headers */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: 1, 
              mb: 1 
            }}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <Box key={day} sx={{ 
                  p: 1, 
                  textAlign: 'center',
                  fontWeight: 600,
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                }}>
                  {day}
                </Box>
              ))}
            </Box>
            
            {/* Calendar Days */}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: 1 
            }}>
              {getCalendarDays(calendarDate).map((day, index) => {
                const isCurrentMonth = day.getMonth() === calendarDate.getMonth();
                const isToday = day.toDateString() === new Date().toDateString();
                const isSelected = day.toDateString() === currentDate.toDateString();
                const hasTimeEntries = getTimeForDate(day) > 0;
                
                return (
                  <Box
                    key={index}
                    onClick={() => handleDateSelect(day)}
                    onDoubleClick={() => handleWeekSelect(day)}
                    sx={{
                      p: 1,
                      textAlign: 'center',
                      cursor: 'pointer',
                      borderRadius: 1,
                      minHeight: '40px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      backgroundColor: isSelected ? '#e67e22' : 'transparent',
                      color: isSelected ? 'white' : 
                             isToday ? '#e67e22' : 
                             isCurrentMonth ? 'text.primary' : 'text.disabled',
                      fontWeight: isToday || isSelected ? 600 : 400,
                      border: isToday && !isSelected ? '2px solid #e67e22' : 'none',
                      '&:hover': {
                        backgroundColor: isSelected ? '#d35400' : '#f5f5f5',
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {day.getDate()}
                    </Typography>
                    {hasTimeEntries && (
                      <Box sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: isSelected ? 'white' : '#27ae60',
                        mt: 0.5
                      }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => {
                setCurrentDate(new Date(currentDate));
                setViewMode('Day');
                setShowDatePicker(false);
              }}
              sx={{
                backgroundColor: '#27ae60',
                '&:hover': {
                  backgroundColor: '#219a52',
                },
                textTransform: 'none',
              }}
            >
              Go to Day View
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setCurrentDate(new Date(currentDate));
                setViewMode('Week');
                setShowDatePicker(false);
              }}
              sx={{
                borderColor: '#e67e22',
                color: '#e67e22',
                '&:hover': {
                  backgroundColor: '#e67e22',
                  color: 'white',
                },
                textTransform: 'none',
              }}
            >
              Go to Week View
            </Button>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            Click a date to select • Double-click to select week • Green dots indicate time entries
          </Typography>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
